import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'

import { isSellOrder } from '@cowprotocol/common-utils'
import { isSolanaChain } from '@cowprotocol/cow-sdk'
import { useTryFindToken } from '@cowprotocol/tokens'
import { StatefulValue } from '@cowprotocol/types'
import { useWalletInfo } from '@cowprotocol/wallet'

import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { useInjectedWidgetParams } from 'entities/injectedWidget'

import { Field } from 'legacy/state/types'
import { useHooksEnabledManager } from 'legacy/state/user/hooks'

import { TradeApproveWithAffectedOrderList, useIsInfiniteApproveDisabled } from 'modules/erc20Approve'
import { EthFlowModal, EthFlowProps } from 'modules/ethFlow'
import { PriceChartToggleButton } from 'modules/priceChart'
import { SELL_ETH_RESET_STATE } from 'modules/swap/consts'
import { AddIntermediateTokenModal } from 'modules/tokensList'
import {
  TradeRows,
  TradeWidget,
  TradeWidgetSlots,
  ButtonsContainer,
  useGetReceiveAmountInfo,
  useIsCustomRecipientLockedOnSolana,
  useIsEoaEthFlow,
  useIsNonEvmBridging,
  useIsWaitAndSaveAvailable,
  useTradePriceImpact,
  useWrapNativeFlow,
} from 'modules/trade'
import { useHandleSwap } from 'modules/tradeFlow'
import { useIsTradeFormValidationPassed, useShouldHideTradeRateDetails } from 'modules/tradeFormValidation'
import { useTradeQuote } from 'modules/tradeQuote'
import { SettingsTab } from 'modules/tradeWidgetAddons'

import { QuoteApiError, QuoteApiErrorCodes } from 'api/cowProtocol/errors/QuoteError'
import { useRateInfoParams } from 'common/hooks/useRateInfoParams'
import { useSafeMemoObject } from 'common/hooks/useSafeMemo'
import { CurrencyInfo } from 'common/pure/CurrencyInputPanel/types'
import { getBridgeIntermediateTokenAddress } from 'common/utils/getBridgeIntermediateTokenAddress'

import { Container } from './styled'

import { useHasEnoughWrappedBalanceForSwap } from '../../hooks/useHasEnoughWrappedBalanceForSwap'
import { useSwapDerivedState } from '../../hooks/useSwapDerivedState'
import {
  useSwapDeadlineState,
  useSwapPartialApprovalToggleState,
  useSwapRecipientToggleState,
  useSwapSettings,
} from '../../hooks/useSwapSettings'
import { useSwapWidgetActions } from '../../hooks/useSwapWidgetActions'
import { useUpdateSwapRawState } from '../../hooks/useUpdateSwapRawState'
import { CrossChainUnlockScreen } from '../../pure/CrossChainUnlockScreen'
import { BottomBanners } from '../BottomBanners/BottomBanners.container'
import { SwapConfirmModal } from '../SwapConfirmModal'
import { SwapDebugPanel } from '../SwapDebugPanel'
import { SwapRateDetails } from '../SwapRateDetails'
import { TradeButtons } from '../TradeButtons'
import { WaitAndSaveRowContainer } from '../WaitAndSaveRow/WaitAndSaveRow.container'
import { Warnings } from '../Warnings'

export interface SwapWidgetProps {
  topContent?: ReactNode
  bottomContent?: ReactNode
  allowSwapSameToken?: boolean
}

const DEFAULT_ENABLED_RECIPIENT: StatefulValue<boolean> = [true, () => void 0]
const DEFAULT_DISABLED_RECIPIENT: StatefulValue<boolean> = [false, () => void 0]

// TODO: Break down this large function into smaller functions
// eslint-disable-next-line max-lines-per-function
export function SwapWidget({ topContent, bottomContent, allowSwapSameToken }: SwapWidgetProps): ReactNode {
  const { showRecipient } = useSwapSettings()
  const deadlineState = useSwapDeadlineState()
  const recipientToggleState = useSwapRecipientToggleState()
  const { chainId } = useWalletInfo()
  const hooksEnabledState = useHooksEnabledManager()
  const isNonEvmBridging = useIsNonEvmBridging()
  const { isLoading: isRateLoading, bridgeQuote, error: quoteError } = useTradeQuote()
  const isFeeExceedsError =
    quoteError instanceof QuoteApiError && quoteError.type === QuoteApiErrorCodes.SellAmountDoesNotCoverFee
  const hideQuoteAmount = useShouldHideTradeRateDetails()
  const priceImpact = useTradePriceImpact()
  const widgetActions = useSwapWidgetActions(hooksEnabledState[0])
  const receiveAmountInfo = useGetReceiveAmountInfo()
  const { disableCustomRecipient } = useInjectedWidgetParams()
  const isRecipientLockedOnSolana = useIsCustomRecipientLockedOnSolana()
  const { token: intermediateBuyToken, toBeImported } = useTryFindToken(getBridgeIntermediateTokenAddress(bridgeQuote))
  const [showNativeWrapModal, setOpenNativeWrapModal] = useState(false)
  const [showAddIntermediateTokenModal, setShowAddIntermediateTokenModal] = useState(false)

  const openNativeWrapModal = useCallback(() => setOpenNativeWrapModal(true), [])
  const dismissNativeWrapModal = useCallback(() => setOpenNativeWrapModal(false), [])

  const wrapCallback = useWrapNativeFlow()
  const updateSwapState = useUpdateSwapRawState()

  const {
    inputCurrency,
    outputCurrency,
    inputCurrencyAmount,
    outputCurrencyAmount,
    inputCurrencyBalance,
    outputCurrencyBalance,
    inputCurrencyFiatAmount,
    outputCurrencyFiatAmount,
    recipient,
    recipientAddress,
    orderKind,
    isUnlocked,
  } = useSwapDerivedState()
  const doTrade = useHandleSwap({ deadline: deadlineState[0] }, widgetActions)
  const hasEnoughWrappedBalanceForSwap = useHasEnoughWrappedBalanceForSwap()
  const handleUnlock = useCallback(() => updateSwapState({ isUnlocked: true }), [updateSwapState])
  const isPrimaryValidationPassed = useIsTradeFormValidationPassed()
  const isEoaEthFlow = useIsEoaEthFlow()

  useEffect(() => {
    if (isEoaEthFlow && !isSellOrder(orderKind)) {
      updateSwapState(SELL_ETH_RESET_STATE)
    }
  }, [isEoaEthFlow, orderKind, updateSwapState])

  const isSellTrade = isSellOrder(orderKind)

  const ethFlowProps: EthFlowProps = useSafeMemoObject({
    nativeInput: inputCurrencyAmount || undefined,
    onDismiss: dismissNativeWrapModal,
    wrapCallback,
    directSwapCallback: doTrade.callback,
    hasEnoughWrappedBalanceForSwap,
  })

  const inputCurrencyInfo: CurrencyInfo = {
    field: Field.INPUT,
    currency: inputCurrency,
    amount: inputCurrencyAmount,
    isIndependent: isSellTrade,
    balance: inputCurrencyBalance,
    fiatAmount: inputCurrencyFiatAmount,
    receiveAmountInfo: !isSellTrade ? receiveAmountInfo : null,
  }

  const outputCurrencyInfo: CurrencyInfo = {
    field: Field.OUTPUT,
    currency: outputCurrency,
    amount: outputCurrencyAmount,
    isIndependent: !isSellTrade,
    balance: outputCurrencyBalance,
    fiatAmount: outputCurrencyFiatAmount,
    receiveAmountInfo: isSellTrade ? receiveAmountInfo : null,
  }

  const inputCurrencyPreviewInfo = {
    amount: inputCurrencyAmount,
    fiatAmount: inputCurrencyFiatAmount,
    balance: inputCurrencyBalance,
    label: isSellTrade ? t`Sell amount` : t`Expected sell amount`,
  }

  const outputCurrencyPreviewInfo = {
    amount: outputCurrencyAmount,
    fiatAmount: outputCurrencyFiatAmount,
    balance: outputCurrencyBalance,
    label: isSellTrade ? t`Receive (before fees)` : t`Buy exactly`,
  }

  const rateInfoParams = useRateInfoParams(inputCurrencyAmount, outputCurrencyAmount)

  const buyingFiatAmount = useMemo(
    () => (isSellTrade ? outputCurrencyInfo.fiatAmount : inputCurrencyInfo.fiatAmount),
    [isSellTrade, outputCurrencyInfo.fiatAmount, inputCurrencyInfo.fiatAmount],
  )

  const hasInputAmount = !!inputCurrencyAmount && !inputCurrencyAmount.equalTo(0)

  const handleImport = useCallback(() => {
    setShowAddIntermediateTokenModal(false)
  }, [])

  const handleCloseImportModal = useCallback(() => {
    setShowAddIntermediateTokenModal(false)
  }, [])

  const isInfiniteApproveDisabled = useIsInfiniteApproveDisabled()
  const enablePartialApprovalState = useSwapPartialApprovalToggleState()
  const showWaitForCows = useIsWaitAndSaveAvailable()

  const slots: TradeWidgetSlots = {
    topContent,
    lockScreen: !isUnlocked ? <CrossChainUnlockScreen handleUnlock={handleUnlock} /> : undefined,
    settingsWidget: (
      <ButtonsContainer>
        <PriceChartToggleButton />
        <SettingsTab
          recipientToggleState={getRecipientToggleState(
            isNonEvmBridging,
            isRecipientLockedOnSolana,
            recipientToggleState,
          )}
          hooksEnabledState={hooksEnabledState}
          deadlineState={deadlineState}
          enablePartialApprovalState={enablePartialApprovalState}
          partialApprovalLocked={isInfiniteApproveDisabled}
          isRecipientToggleDisabled={isNonEvmBridging || isRecipientLockedOnSolana}
          isRecipientToggleHidden={disableCustomRecipient}
          recipientToggleDisabledNote={
            isRecipientLockedOnSolana ? <Trans>Custom recipients are not available on Solana yet.</Trans> : undefined
          }
          isHooksToggleDisabled={isSolanaChain(chainId)}
        />
      </ButtonsContainer>
    ),
    bottomContent: useCallback(
      (tradeWarnings: ReactNode | null, captcha: ReactNode | null) => {
        return (
          <>
            {bottomContent}

            <TradeRows>
              {(!hideQuoteAmount || (isFeeExceedsError && !isRateLoading && hasInputAmount)) && (
                <SwapRateDetails rateInfoParams={rateInfoParams} deadline={deadlineState[0]} />
              )}
              {isPrimaryValidationPassed && <TradeApproveWithAffectedOrderList />}
            </TradeRows>

            {showWaitForCows && <WaitAndSaveRowContainer />}
            <Warnings buyingFiatAmount={buyingFiatAmount} hideQuoteAmount={hideQuoteAmount} />
            {tradeWarnings}
            {captcha}
            <TradeButtons
              isTradeContextReady={doTrade.contextIsReady}
              openNativeWrapModal={openNativeWrapModal}
              hasEnoughWrappedBalanceForSwap={hasEnoughWrappedBalanceForSwap}
              tokenToBeImported={toBeImported}
              intermediateBuyToken={intermediateBuyToken}
              setShowAddIntermediateTokenModal={setShowAddIntermediateTokenModal}
            />
          </>
        )
      },
      [
        bottomContent,
        rateInfoParams,
        deadlineState,
        buyingFiatAmount,
        doTrade.contextIsReady,
        openNativeWrapModal,
        hasEnoughWrappedBalanceForSwap,
        toBeImported,
        intermediateBuyToken,
        isPrimaryValidationPassed,
        showWaitForCows,
        hideQuoteAmount,
        isFeeExceedsError,
        hasInputAmount,
        isRateLoading,
      ],
    ),
  }

  const params = {
    compactView: true,
    enableSmartSlippage: true,
    enableSellEqBuy: hooksEnabledState[0],
    isMarketOrderWidget: true,
    isSellingEthSupported: true,
    allowSwapSameToken,
    recipient,
    showRecipient,
    isTradePriceUpdating: isRateLoading,
    priceImpact,
  }

  return (
    <Container>
      <SwapDebugPanel contextIsReady={doTrade.contextIsReady} deadline={deadlineState[0]} />
      {showAddIntermediateTokenModal ? (
        <AddIntermediateTokenModal
          onDismiss={handleCloseImportModal}
          onBack={handleCloseImportModal}
          onImport={handleImport}
        />
      ) : (
        <TradeWidget
          slots={slots}
          actions={widgetActions}
          params={params}
          inputCurrencyInfo={inputCurrencyInfo}
          outputCurrencyInfo={outputCurrencyInfo}
          confirmModal={
            <SwapConfirmModal
              doTrade={doTrade.callback}
              isTradeContextReady={doTrade.contextIsReady}
              recipient={recipient}
              recipientAddress={recipientAddress}
              priceImpact={priceImpact}
              inputCurrencyInfo={inputCurrencyPreviewInfo}
              outputCurrencyInfo={outputCurrencyPreviewInfo}
            />
          }
          genericModal={showNativeWrapModal && <EthFlowModal {...ethFlowProps} />}
        />
      )}
      <BottomBanners />
    </Container>
  )
}

// Non-EVM bridging locks the toggle on (the recipient is required); Solana locks it off. Both ignore
// the stored setting so a value carried over from an EVM chain can't show as enabled.
function getRecipientToggleState(
  isNonEvmBridging: boolean,
  isLockedOnSolana: boolean,
  toggleState: StatefulValue<boolean>,
): StatefulValue<boolean> {
  if (isNonEvmBridging) return DEFAULT_ENABLED_RECIPIENT
  if (isLockedOnSolana) return DEFAULT_DISABLED_RECIPIENT

  return toggleState
}
